import { AssetProps } from '../../helpers/types';
import { useScreenDetector } from '../../hooks';
import { EnvironmentContext } from '../../contexts';
import { useContext } from 'react';
import { Link, LinkProps } from '../Link/Link';
import classNames from 'classnames';
import { ProjectTypes, getProjectType } from '@r-and-a-shared-ui/utils';

import stylesPP from './scss/HeroBannerPP.module.scss';
import stylesMMB from './scss/HeroBannerMMB.module.scss';

const projectType = getProjectType();
const styles =
  projectType === ProjectTypes.PlayerPortals ? stylesPP : stylesMMB;

type ButtonType = {
  link?: LinkProps;
  ctaText?: string;
};

export interface HeroBannerProps {
  title?: string;
  subtitle?: string;
  description?: string;
  backgroundImage?: AssetProps;
  mobileImage?: AssetProps;
  buttons?: (ButtonType | undefined)[];
  kontentItemId?: string;
}

export function HeroBanner({
  title,
  subtitle,
  description,
  backgroundImage,
  mobileImage,
  buttons,
  kontentItemId,
}: HeroBannerProps) {
  const titleParts = title ? title.trim().split(/\s+/) : [];
  const lastTitleWord = titleParts.length ? titleParts.pop()! : '';
  const titleRest = titleParts.join(' ');

  return (
    <div
      className={`${
        projectType === ProjectTypes.PlayerPortals
          ? styles.wrapper
          : styles.container
      }`}
      aria-labelledby="hero-title hero-subtitle"
      aria-describedby="hero-description"
      data-kontent-item-id={kontentItemId}
    >
      <div className={styles.card}>
        <HeroBannerImageComponent
          backgroundImage={backgroundImage}
          mobileImage={mobileImage}
        />
        <div
          className={`${styles.card__content} ${
            projectType === ProjectTypes.PlayerPortals ? styles.container : ''
          }`}
        >
          {title && (
            <h2
              id="hero-title"
              className={`${styles.card__content__title} ${
                subtitle ? styles.title__faded : ''
              }`}
            >
              {titleRest && <>{titleRest} </>}
              <span className={styles.title__lastWord}>
                {lastTitleWord}
                {subtitle && (
                  <span
                    className={styles.title__lastWord__separator}
                    aria-hidden="true"
                  ></span>
                )}
              </span>
            </h2>
          )}
          {subtitle && (
            <h3 id="hero-subtitle" className={styles.card__content__subtitle}>
              {subtitle}
            </h3>
          )}
          {buttons && (
            <div className={styles.card__content__link}>
              {buttons.map((button: ButtonType | undefined, index: number) => (
                <HeroBannerButtonComponent key={index} button={button} />
              ))}
            </div>
          )}
          {description && (
            <div className={styles.card__content__description}>
              {description && (
                <p id="hero-description" className={styles.description}>
                  {description}
                </p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default HeroBanner;

const HeroBannerImageComponent = ({
  backgroundImage,
  mobileImage,
}: {
  backgroundImage?: AssetProps;
  mobileImage?: AssetProps;
}) => {
  const { Image } = useContext(EnvironmentContext);
  const { isMobile } = useScreenDetector();

  const heroBannerImage = isMobile ? mobileImage : backgroundImage;
  const width = isMobile ? 390 : 762;
  const height = isMobile ? 316 : 546;

  return (
    <div className={styles.card__background}>
      <Image
        src={heroBannerImage?.url || ''}
        alt={heroBannerImage?.description || ''}
        width={width}
        height={height}
        sizes="100vw"
      />
    </div>
  );
};

const HeroBannerButtonComponent = ({ button }: { button?: ButtonType }) => {
  if (!button) {
    return null;
  }

  const ctaLink = {
    title: button.ctaText || button.link?.title || '',
    internal: !!button.link?.internal,
    openInNewTab: !!button.link?.openInNewTab,
    href: button.link?.href || '',
  };

  if (!ctaLink.href.length) {
    return null;
  }

  return (
    <Link
      className={classNames(styles.btn, styles['btn--secondary'], {
        [styles['inactive-link']]: !ctaLink?.href?.length,
      })}
      {...ctaLink}
    />
  );
};
